import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { log } from 'src/app/core/log';
import { environment } from 'src/environments/environment';

/**
 * A donde vuelve la app tras recargar: la comanda. Ionic crea cada pestaña recien cuando se
 * la visita, y una recarga en analiticas o en la cuenta dejaba sin crear la lista de pedidos,
 * que es la que escucha y hace sonar los pedidos nuevos.
 */
const COMANDA_URL = '/tabs/tab1';

/** Cada cuanto se mira si hubo un deploy. */
const CHECK_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Las paginas que solo muestran datos: recargarlas no pierde nada. Las demas tienen
 * formularios (un plato, el perfil, la vitrina) que pasan mucho tiempo sin un campo con el
 * foco, y recargarlas tiraria lo no guardado.
 */
const RELOADABLE_PAGES = ['/tabs/', '/orders', '/analytics', '/account'];

/**
 * Lo que se monta encima de la app: recargar con uno abierto pierde lo que se hacia. Los
 * overlays de Ionic 8 (`ion-picker` es el picker en linea; el overlay es `ion-picker-legacy`)
 * y los avisos de SweetAlert2, que usa la lista de pedidos.
 */
const OVERLAYS =
  'ion-modal, ion-alert, ion-action-sheet, ion-loading, ion-popover, ion-picker-legacy, .swal2-container';

/**
 * Recarga la app cuando se publico una version nueva.
 *
 * Firebase Hosting sirve solo la ultima version, y los componentes de Ionic (`ion-alert`,
 * `ion-toast`, …) viven en chunks que se piden la primera vez que se usan. Una comanda
 * abierta todo el dia, despues de un deploy, pide un chunk que ya no existe; la
 * reescritura a `index.html` devuelve HTML y el aviso no aparece. Por eso se comparan los
 * bundles que pide el `index.html` publicado (con hash en el nombre) con los que cargo la
 * pestaña y, si hay uno nuevo, se recarga en cuanto el comercio este en una pagina que solo
 * muestra datos (la lista de pedidos, sobre todo), sin nada abierto encima ni un campo con el
 * foco. Si esta en un formulario, la recarga espera a que vuelva a una lista.
 *
 * Solo vigila en produccion: el build de develop sale sin hash, sus bundles se llaman
 * igual en cada deploy y no habria nada que detectar.
 */
@Injectable({ providedIn: 'root' })
export class UpdateWatcherService implements OnDestroy {
  private pending = false;
  private interval?: ReturnType<typeof setInterval>;
  private navigation?: Subscription;
  private readonly onVisibility = () => {
    if (document.visibilityState === 'visible') {
      void this.check();
    }
  };

  constructor(private router: Router) {}

  /** Arranca la vigilancia. Fuera de produccion (develop, `ng serve`, las pruebas) no hace nada. */
  start(): void {
    if (!this.shouldWatch()) {
      return;
    }
    void this.check();
    this.interval = setInterval(() => void this.check(), CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', this.onVisibility);
    // Con una recarga pendiente, sale apenas el comercio vuelve de un formulario a una lista.
    this.navigation = this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      if (this.pending) {
        void this.check();
      }
    });
  }

  /**
   * Suelta el intervalo y las escuchas. En la app no pasa (el servicio vive lo que la
   * pagina); en las pruebas, sin esto una instancia vieja con una recarga pendiente
   * navegaba la pagina de Karma al primer `visibilitychange` de otra prueba.
   */
  ngOnDestroy(): void {
    clearInterval(this.interval);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.navigation?.unsubscribe();
  }

  /** Compara con lo publicado; si hay version nueva, recarga cuando la pantalla este libre. */
  async check(): Promise<void> {
    if (!this.pending) {
      const html = await this.fetchIndex();
      if (html === null) {
        return;
      }
      this.pending = this.isNewVersion(html);
    }
    if (this.pending && this.onReloadablePage() && this.isIdle()) {
      this.openUrl(COMANDA_URL);
    }
  }

  protected shouldWatch(): boolean {
    return environment.production;
  }

  /**
   * Si el `index.html` publicado pide un bundle que esta pestaña no cargo. Se compara con la
   * pagina cargada y no con una primera lectura, para que un deploy anterior a esa lectura
   * tambien cuente. Los chunks que la app agrega despues de cargar no estorban: solo importa
   * que todo lo publicado este cargado.
   */
  private isNewVersion(html: string): boolean {
    const published = scriptSources(new DOMParser().parseFromString(html, 'text/html'));
    const loaded = new Set(this.loadedScripts());
    return published.some((src) => !loaded.has(src));
  }

  /** Una pagina que solo muestra datos, sin formulario que se pierda al recargar. */
  private onReloadablePage(): boolean {
    const path = this.router.url.split(/[?#]/)[0];
    return RELOADABLE_PAGES.some((page) => path.startsWith(page));
  }

  /** El `index.html` publicado, salteando la cache. `null` si no se pudo leer. */
  protected async fetchIndex(): Promise<string | null> {
    try {
      const response = await fetch('/index.html', { cache: 'no-store' });
      return response.ok ? await response.text() : null;
    } catch (error) {
      log('no se pudo comprobar la version publicada', error);
      return null;
    }
  }

  /** Sin un overlay presentado ni un campo con el foco. */
  protected isIdle(): boolean {
    const active = document.activeElement;
    const typing =
      active instanceof HTMLInputElement ||
      active instanceof HTMLTextAreaElement ||
      (active instanceof HTMLElement && active.isContentEditable);
    const presented = Array.from(document.querySelectorAll(OVERLAYS)).some(
      (overlay) => !overlay.classList.contains('overlay-hidden')
    );
    return !typing && !presented;
  }

  /** Los bundles con los que cargo esta pestaña. */
  protected loadedScripts(): string[] {
    return scriptSources(document);
  }

  /** Carga completa (no navegacion del router): trae el `index.html` y los bundles nuevos. */
  protected openUrl(url: string): void {
    location.replace(url);
  }
}

/** El `src` tal como esta escrito en cada `<script>` con `src` del documento. */
function scriptSources(doc: Document): string[] {
  return Array.from(doc.querySelectorAll('script[src]')).map((script) => script.getAttribute('src') ?? '');
}
